import { useState } from "react";
import { motion } from "framer-motion";
import { X, Building2, User } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useLang } from "@/contexts/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls = "w-full bg-[#1A1A1A] border border-[#262626] px-5 py-3.5 text-white font-['Outfit'] font-light text-sm placeholder:text-white/25 focus:outline-none focus:border-[#C5A059]/50 transition-colors";

export default function CheckoutModal({ event, pkg, pkgIndex, quantity, onClose }) {
  const { t, lang } = useLang();
  const [buyerType, setBuyerType] = useState("company");
  const [paymentMethod, setPaymentMethod] = useState("card");
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    customer_company: "",
    org_number: "",
    vat_number: "",
    billing_address: "",
    billing_zip: "",
    billing_city: "",
    billing_country: "Sweden",
    reference: "",
    message: "",
  });

  const total = pkg.price * quantity;

  let submitLabel = t("submit_invoice");
  if (submitting) submitLabel = t("processing");
  else if (paymentMethod === "card") submitLabel = t("proceed_payment");

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.customer_name || !form.customer_email) return;
    setSubmitting(true);
    try {
      if (paymentMethod === "card") {
        const res = await axios.post(`${API}/checkout`, {
          event_slug: event.slug,
          package_index: pkgIndex,
          quantity,
          ...form,
          buyer_type: buyerType,
          payment_method: "card",
          origin_url: window.location.origin,
          lang,
        });
        if (res.data.checkout_url) window.location.href = res.data.checkout_url;
      } else {
        const res = await axios.post(`${API}/invoice-request`, {
          event_slug: event.slug,
          package_index: pkgIndex,
          quantity,
          ...form,
          buyer_type: buyerType,
          lang,
        });
        toast.success(`${t("invoice_request_sent") || "Fakturaförfrågan skickad!"} — ${res.data.order_number}`);
        onClose();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || t("something_wrong") || "Något gick fel");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        data-testid="checkout-modal"
        className="relative bg-[#141414] border border-[#262626] max-w-2xl w-full z-10 max-h-[95vh] overflow-y-auto"
      >
        <button onClick={onClose} data-testid="close-checkout-modal" className="absolute top-4 right-4 text-white/40 hover:text-white z-10">
          <X size={20} />
        </button>

        <div className="p-8">
          {/* Header */}
          <span className="text-xs tracking-[0.3em] uppercase text-[#C5A059] font-['Outfit'] font-semibold block mb-2">
            {t("checkout_title")}
          </span>
          <h3 className="font-['Cormorant_Garamond'] text-2xl font-medium text-white mb-1">{pkg.name}</h3>
          <p className="text-white/50 font-['Outfit'] font-light text-sm mb-6">
            {event.title} — {event.date} · {quantity}x · <span className="text-white font-medium">{total.toLocaleString()} {pkg.currency}</span>
          </p>

          {/* Buyer type toggle */}
          <div className="flex gap-2 mb-6">
            <button
              type="button"
              onClick={() => setBuyerType("company")}
              data-testid="buyer-type-company"
              className={`flex items-center gap-2 px-5 py-3 text-xs tracking-wider uppercase font-['Outfit'] border transition-all flex-1 ${
                buyerType === "company" ? "border-[#C5A059] text-[#C5A059] bg-[#C5A059]/5" : "border-[#262626] text-white/50"
              }`}
            >
              <Building2 size={16} /> {t("company_label")}
            </button>
            <button
              type="button"
              onClick={() => setBuyerType("private")}
              data-testid="buyer-type-private"
              className={`flex items-center gap-2 px-5 py-3 text-xs tracking-wider uppercase font-['Outfit'] border transition-all flex-1 ${
                buyerType === "private" ? "border-[#C5A059] text-[#C5A059] bg-[#C5A059]/5" : "border-[#262626] text-white/50"
              }`}
            >
              <User size={16} /> {t("private_label")}
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-3">
            {/* Company fields */}
            {buyerType === "company" && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <input name="customer_company" value={form.customer_company} onChange={handleChange} placeholder={t("company_name")} required data-testid="checkout-company" className={inputCls} />
                  <input name="org_number" value={form.org_number} onChange={handleChange} placeholder={t("org_number")} required data-testid="checkout-org-number" className={inputCls} />
                </div>
                <input name="vat_number" value={form.vat_number} onChange={handleChange} placeholder={t("vat_number")} data-testid="checkout-vat" className={inputCls} />
              </>
            )}

            {/* Contact person */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <input name="customer_name" value={form.customer_name} onChange={handleChange} placeholder={buyerType === "company" ? t("contact_person") : t("full_name")} required data-testid="checkout-name" className={inputCls} />
              <input name="customer_email" type="email" value={form.customer_email} onChange={handleChange} placeholder={t("email")} required data-testid="checkout-email" className={inputCls} />
            </div>
            <input name="customer_phone" value={form.customer_phone} onChange={handleChange} placeholder={t("phone")} data-testid="checkout-phone" className={inputCls} />

            {/* Reference (for companies) */}
            {buyerType === "company" && (
              <input name="reference" value={form.reference} onChange={handleChange} placeholder={t("reference")} data-testid="checkout-reference" className={inputCls} />
            )}

            {/* Billing address */}
            <div className="pt-2">
              <p className="text-white/40 text-xs font-['Outfit'] uppercase tracking-wider mb-3">{t("billing_address")}</p>
              <div className="space-y-3">
                <input name="billing_address" value={form.billing_address} onChange={handleChange} placeholder={t("street")} data-testid="checkout-address" className={inputCls} />
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  <input name="billing_zip" value={form.billing_zip} onChange={handleChange} placeholder={t("zip")} data-testid="checkout-zip" className={inputCls} />
                  <input name="billing_city" value={form.billing_city} onChange={handleChange} placeholder={t("city")} data-testid="checkout-city" className={inputCls} />
                  <input name="billing_country" value={form.billing_country} onChange={handleChange} placeholder={t("country")} data-testid="checkout-country" className={inputCls} />
                </div>
              </div>
            </div>

            {/* Payment method */}
            <div className="pt-4">
              <p className="text-white/40 text-xs font-['Outfit'] uppercase tracking-wider mb-3">{t("payment_method")}</p>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setPaymentMethod("card")}
                  data-testid="payment-method-card"
                  className={`px-4 py-3.5 text-xs tracking-wider uppercase font-['Outfit'] border transition-all ${
                    paymentMethod === "card" ? "border-[#C5A059] text-[#C5A059] bg-[#C5A059]/5" : "border-[#262626] text-white/50"
                  }`}
                >
                  {t("pay_card")}
                </button>
                <button
                  type="button"
                  onClick={() => setPaymentMethod("invoice")}
                  data-testid="payment-method-invoice"
                  className={`px-4 py-3.5 text-xs tracking-wider uppercase font-['Outfit'] border transition-all ${
                    paymentMethod === "invoice" ? "border-[#C5A059] text-[#C5A059] bg-[#C5A059]/5" : "border-[#262626] text-white/50"
                  }`}
                >
                  {t("request_invoice")}
                </button>
              </div>
            </div>

            {/* Message for invoice */}
            {paymentMethod === "invoice" && (
              <textarea name="message" value={form.message} onChange={handleChange} placeholder={t("invoice_notes")} rows={2} data-testid="checkout-message" className={`${inputCls} resize-none`} />
            )}

            {/* Summary */}
            <div className="bg-[#0A0A0A] border border-[#262626] p-5 mt-4 space-y-2 text-sm font-['Outfit']">
              <div className="flex justify-between items-center">
                <span className="text-white/50">{pkg.name} x {quantity}</span>
                <span className="text-white">{total.toLocaleString('sv-SE')} {pkg.currency}</span>
              </div>
              {paymentMethod === "card" && (
                <>
                  <div className="flex justify-between items-center text-white/50">
                    <span>Moms (25%)</span>
                    <span>{(total * 0.25).toLocaleString('sv-SE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {pkg.currency}</span>
                  </div>
                  <div className="flex justify-between items-center border-t border-[#262626] pt-2 mt-1">
                    <span className="text-white/70">Totalt (inkl moms)</span>
                    <span className="text-white font-semibold text-lg">{(total * 1.25).toLocaleString('sv-SE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {pkg.currency}</span>
                  </div>
                </>
              )}
              {paymentMethod === "invoice" && (
                <div className="flex justify-between items-center border-t border-[#262626] pt-2 mt-1">
                  <span className="text-white/70">Totalt (ex moms)</span>
                  <span className="text-white font-semibold text-lg">{total.toLocaleString('sv-SE')} {pkg.currency}</span>
                </div>
              )}
              <p className="text-white/30 text-[11px] pt-1">
                {paymentMethod === "card"
                  ? "25% moms tillkommer på kortbetalningar."
                  : "Moms hanteras vid fakturering."}
              </p>
            </div>

            <button
              type="submit"
              disabled={submitting}
              data-testid="checkout-submit-btn"
              className="btn-primary w-full py-4 text-xs tracking-[0.2em] uppercase font-['Outfit'] font-semibold disabled:opacity-50 mt-2"
            >
              {submitLabel}
            </button>
          </form>
        </div>
      </motion.div>
    </div>
  );
}
